import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import { Box } from "@chakra-ui/react";
import { UserProvider, useUser } from "./components/userRole/UserContext";
import {
  useGoogleAuth,
  GoogleAuthProvider,
} from "./components/common/google/GoogleAuthContext";
import { OTPProvider } from "./components/AdminPanelAccess/OTPContext";
import { ScrollProvider } from "./components/ScrollContext";
import { CurrencyProvider } from "./components/CurrencyExchange/CurrencyContext";
import { TranslationProvider } from "./components/LanguageTranslation/TranslationContext";
import HomePage from "./components/Home";
import LoginPage from "./components/Login";
import BusinessPage from "./components/BusinessPage";
import CreatorPage from "./components/CreatorPage";
import NextAdventure from "./components/NextAdventure";
import ProtectedRoute from "./components/ProtectedRoute";
import ForgotPassword from "./components/ForgotPassword";
import ResetPassword from "./components/ResetPassword";
import PrivacyPolicy from "./components/PrivacyPolicy";
import PrivacyPolicyNL from "./components/PrivacyPolicyNL";
import TermsOfService from "./components/TermsOfService";
import Disclaimer from "./components/Disclaimer";
import Chat from "./components/chat/Chat";
import UserAccount from "./components/AccountSettings/UserAccount";
import PackingListMenu from "./components/PackingList/PackingListMenu";
import Itinerary from "./components/Itinerary/Itinerary";
import TravelTracker from "./components/TravelTracker/TravelTracker";
import BackToTrips from "./components/TravelTracker/BackToTrips";
import PostDetail from "./components/TravelTracker/Posts/PostDetail";
import SearchedUser from "./components/TravelTracker/SearchedUser/SearchedUser";
import Blog from "./components/Blog/Blog";
import BlogDetail from "./components/Blog/BlogDetail";
import BlogPreview from "./components/Blog/BlogPreview";
import BucketList from "./components/BucketList/BucketList";
import BudgetPlanner from "./components/BudgetPlanner/BudgetPlanner";
import ItineraryDetail from "./components/BucketList/ItineraryDetail";
// import PrivacySettings from "./components/SecondaryPages/PrivacySettings";
import SideBar from "./components/Sidebar/Sidebar";
import ContactUs from "./components/ContactUs";
import RoleManagement from "./components/userRole/RoleManagement";
import AdminFeedbackSurvey from "./components/AdminFeedbackSurvey/AdminFeedbackSurvey";
import SurveyFeedback from "./components/AdminFeedbackSurvey/SurveyFeedback";
import Filters from "./components/Filters/Filters";
import UpdateBlog from "./components/Blog/UpdateBlog";
import AdminBlogUpload from "./components/Blog/AdminBlogUpload";
import AdminTimeAnalytics from "./components/AdminTimeAnalytics/AdminTimeAnalytics";
import BlogFilters from "./components/Blog/BlogFilters";
import BlogTitlesList from "./components/Blog/BlogTitlesList";
import Newsletter from "./components/AdminNewsletter/Newsletter";
import UserForm from "./components/AdminUserFrom/UserForm";
import usePageTracking from "./components/usePageTracking";
import DonationSuccess from "./components/Donation/DonationSuccess";
import DonationFailure from "./components/Donation/DonationFailure";
import ChatArchive from "./components/SecondaryPages/ChatArchive";
import ReportProblem from "./components/SecondaryPages/ReportProblem";
import Dashboard from "./components/AdminPanel/Dashboard";
import AdminSidebar from "./components/AdminPanel/Sidebar";
import AdminMailbox from "./components/AdminPanel/AdminMailbox";
import TermsOfServiceNL from "./components/TermsOfServiceNL";
import { useAuth } from "./auth";
import { HelmetProvider } from "react-helmet-async";
import ScrollToTop from "./components/common/ScrollToTop";
import AccountSettings from "./components/AccountSettings/AccountSettings";
import Contribution from "./components/Contribution/Contribution";
import AllUsersNotificationPreferences  from "./components/AccountSettings/AllUsersNotificationPreferences";
import AdminReportProblem from "./components/AdminPanel/AdminReportProblem";
import EmailVerification from "./components/EmailVerification";
import AdminProtectedRoute from "./components/AdminPanelAccess/AdminProtectedRoute";
import NotFound from "./components/NotFound";
import AdminPanelAccess from "./components/AdminPanelAccess/AdminPanelAccess";

const AppContent = () => {
  const location = useLocation();
  const history = useHistory();
  const { login } = useGoogleAuth();
  const { user, setUser } = useUser();
  const [isLoginSuccessful, setIsLoginSuccessful] = useState(false);
  const [logged] = useAuth();
  const {
    authState: { isLoggedIn },
  } = useGoogleAuth();
  const isUserLoggedIn = logged || isLoggedIn;
  
  // Enable page tracking for analytics
  usePageTracking();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const access_token = params.get("access_token");
    const refresh_token = params.get("refresh_token");
    const email = params.get("email");
    const username = params.get("username");
    const role = params.get("role");

    if (access_token) {
      login(access_token);
      const userData = {
        access_token,
        refresh_token,
        email,
        username,
        role: role,
      };
      setUser(userData);
      localStorage.setItem("REACT_TOKEN_AUTH_KEY", JSON.stringify(userData));
      localStorage.setItem("email", email);
      localStorage.setItem("username", username);
      history.push("/ai-travel-planner");
      setIsLoginSuccessful(true);
    }
  }, [window.location.search]);

  // Helper function to check if current path matches any defined route
  const isValidRoute = () => {
    const validPaths = [
      "/",
      "/auth/verify_email/",
      "/home",
      "/businesses", 
      "/creators",
      "/contact",
      "/forgot_password",
      "/auth/reset_password/",
      "/policies/privacy-policy",
      "/policies/privacy-policy/nl",
      "/policies/terms-of-service",
      "/policies/terms-of-service/nl",
      "/disclaimer",
      "/ai-travel-planner",
      "/blogs",
      "/admin",
      "/user-account",
      "/account-settings",
      "/settings",
      "/travel-tracker",
      "/contributions",
      "/donations",
      "/get-started",
      "/signup",
      "/login",
      "/offset-your-carbon-footprint",
      "/chat-archive",
      "/report-problem",
      "/archive",
      "/report"
    ];
    
    // Check exact matches and path prefixes
    return validPaths.some((path) => {
      if (path === "/") {
        return location.pathname === "/";
      }
      return location.pathname.startsWith(path);
    });
  };

  const shouldHideSidebar = () => {
    // Always hide sidebar on 404 pages (invalid routes)
    if (!isValidRoute()) return true;
    
    if (location.pathname === "/") return true;
    const paths = [
      "/get-started",
      "/forgot_password",
      "/auth/reset_password/",
      "/signup",
      "/policies/privacy-policy",
      "/policies/privacy-policy/nl",
      "/policies/terms-of-service",
      "/policies/terms-of-service/nl",
      "/disclaimer",
      "/home",
      "/blogs/:id",
      "/blogs",
      "/admin/blog/preview",
      "/contact",
      "/businesses",
      "/creators",
      "/login",
      "/offset-your-carbon-footprint",
      "/contributions/success",
      "/contributions/failure",
      "/auth/verify_email/",
      "/admin/2fa",
    ];
    return paths.some((path) => location.pathname.startsWith(path));
  };

  const shouldShowAdminSidebar = () => {
    const paths = ["/admin"];
    const excluded = ["/admin/blog/preview", "/admin/2fa"];
    return (
      paths.some((path) => location.pathname.startsWith(path)) &&
      !excluded.some((path) => location.pathname.startsWith(path))
    );
  };

  //TODO REMOVE BEFORE LAUNCH. ALSO REMOVE LOGIN PAGE
  const publicPages = ["/login"];

  if (
    !isUserLoggedIn &&
    !publicPages.some((path) => location.pathname.startsWith(path))
  ) {
    return <Redirect to="/login" />;
  }

  return (
    <Box display="flex" overflowY="hidden">
      {!shouldHideSidebar() && !shouldShowAdminSidebar() && <SideBar />}
      {shouldShowAdminSidebar() && <AdminSidebar />}
      <Box flex="1" transition="margin 0.2s ease-in-out">
        <Switch>
          <Route exact path="/">
            <HomePage />
          </Route>
          <Route path="/auth/verify_email/:token">
            <EmailVerification />
          </Route>
          <Route path="/login">
            <LoginPage />
          </Route>
          <Route path="/home">
            <HomePage />
          </Route>
          <Route path="/businesses">
            <BusinessPage />
          </Route>

          <Route path="/creators">
            <CreatorPage />
          </Route>

          <Route exact path="/">
            <Redirect to="/login" />
          </Route>

          <Route path="/contact">
            <ContactUs />
          </Route>

          <Route path="/forgot_password">
            <ForgotPassword />
          </Route>
          <Route path="/auth/reset_password/:token">
            <ResetPassword />
          </Route>

          <Route path="/policies/privacy-policy/nl">
            <PrivacyPolicyNL />
          </Route>
          <Route path="/policies/privacy-policy">
            <PrivacyPolicy />
          </Route>
          <Route path="/policies/terms-of-service/nl">
            <TermsOfServiceNL />
          </Route>

          <Route path="/policies/terms-of-service">
            <TermsOfService />
          </Route>

          <Route path="/disclaimer">
            <Disclaimer />
          </Route>
          {/* <Route path="/ai-travel-planner">
            <NextAdventure />
          </Route> */}

          {/* <Route path="/ai-travel-planner/chat">
            <Chat />
          </Route> */}

          <Route
            path="/ai-travel-planner/chat"
            component={Chat}
            roles={["Admin", "User"]}
          />

          <Route path="/blogs/:id">
            <BlogDetail />
          </Route>

          {/* <ProtectedRoute
            path="/blogs/:id"
            component={BlogDetail}
            roles={["Admin", "User"]}
          /> */}

          <Route path="/blogs">
            <Blog />
          </Route>

          {/* <ProtectedRoute
            path="/blogs"
            component={Blog}
            roles={["Admin", "User"]}
          /> */}

          <Route path="/offset-your-carbon-footprint/success">
            <DonationSuccess />
          </Route>
          {/* <ProtectedRoute
            path="/contributions/success"
            component={DonationSuccess}
            roles={["Admin", "User"]}
          /> */}
          <Route path="/offset-your-carbon-footprint/failure">
            <DonationFailure />
          </Route>
          {/* <ProtectedRoute
            path="/contributions/failure"
            component={DonationFailure}
            roles={["Admin", "User"]}
          /> */}
          <Route path="/offset-your-carbon-footprint">
            <Contribution />
          </Route>
          {/* <ProtectedRoute
            path="/contributions"
            component={DonationForm}
            roles={["Admin", "User"]}
          /> */}

          <ProtectedRoute
            path="/archive"
            component={ChatArchive}
            roles={["Admin", "User", "Creator", "Business"]}
          />
          <ProtectedRoute
            path="/report"
            component={ReportProblem}
            roles={["Admin", "User", "Creator", "Business"]}
          />

          <ProtectedRoute
            path="/backtotrips/:tripId"
            roles={["Admin", "User", "Creator", "Business"]}
          >
            <BackToTrips />
          </ProtectedRoute>
          <Route
            path="/post/:postId"
            roles={["Admin", "User", "Creator", "Business"]}
          >
            <PostDetail />
          </Route>
          <Route path="/ai-travel-planner/ongoing-trip">
            <Itinerary />
          </Route>
          <Route path="/ai-travel-planner/manage-itinerary/:tripId">
            <ItineraryDetail />
          </Route>

          {/* Admin Routes */}
          <Route path="/admin/2fa" component={AdminPanelAccess} />
          <AdminProtectedRoute path="/admin/dashboard" component={Dashboard} />
          <AdminProtectedRoute
            path="/admin/analytics"
            component={AdminTimeAnalytics}
          />
          <AdminProtectedRoute
            path="/admin/report/problem"
            component={AdminReportProblem}
          />
          <AdminProtectedRoute path="/admin/userform" component={UserForm} />
          <AdminProtectedRoute path="/admin/mailbox" component={AdminMailbox} />
          <AdminProtectedRoute
            path="/admin/user-management"
            component={RoleManagement}
          />
          <AdminProtectedRoute
            path="/admin/feedback"
            component={AdminFeedbackSurvey}
          />
          <AdminProtectedRoute
            path="/admin/feedback-response"
            component={SurveyFeedback}
          />
          <AdminProtectedRoute
            path="/admin/newsletters"
            component={Newsletter}
          />
          <AdminProtectedRoute path="/admin/filter" component={Filters} />
          <AdminProtectedRoute
            path="/admin/blog/upload"
            component={AdminBlogUpload}
          />
          <Route path="/admin/blog/preview">
            <BlogPreview />
          </Route>
          <AdminProtectedRoute
            path="/admin/blog/filters"
            component={BlogFilters}
          />
          <AdminProtectedRoute
            path="/admin/all-blog"
            component={BlogTitlesList}
          />
          <AdminProtectedRoute
            path="/admin/update-blog/:id"
            component={UpdateBlog}
          />
          <AdminProtectedRoute
            path="/admin/notifications"
            component={AllUsersNotificationPreferences}
          />
          {/* End of Admin Routes */}

          <ProtectedRoute
            path="/ai-travel-planner/bucket-list"
            component={BucketList}
            roles={["Admin", "User", "Creator", "Business"]}
          />
          <ProtectedRoute
            path="/search-user/:socialId"
            component={SearchedUser}
            roles={["Admin", "User", "Creator", "Business"]}
          />
          <ProtectedRoute
            path="/ai-travel-planner/travel-feed"
            component={TravelTracker}
            roles={["Admin", "User", "Creator", "Business"]}
          />
          <ProtectedRoute
            path="/account-settings"
            component={UserAccount}
            roles={["Admin", "User", "Creator", "Business"]}
          />
          <ProtectedRoute
            path="/settings/account"
            component={AccountSettings}
            roles={["Admin", "User", "Creator", "Business"]}
          />
          <Route path="/ai-travel-planner/packing-list/:tripId">
            <PackingListMenu />
          </Route>
          <ProtectedRoute
            path="/ai-travel-planner/packing-list"
            component={PackingListMenu}
            roles={["Admin", "User", "Creator", "Business"]}
          />

          {/* <Route path="/ai-travel-planner/packing-list">
            <PackingListMenu />
          </Route> */}
          <Route path="/ai-travel-planner/budget-planner/:tripId">
            <BudgetPlanner />
          </Route>
          <ProtectedRoute
            path="/ai-travel-planner/budget-planner"
            component={BudgetPlanner}
            roles={["Admin", "User", "Creator", "Business"]}
          />

          {/* <ProtectedRoute
            path="/settings/privacy"
            component={PrivacySettings}
            roles={["Admin", "User"]}
          /> */}
          <Route path="/ai-travel-planner">
            <NextAdventure />
          </Route>
          {/* <ProtectedRoute
            path="/ai-travel-planner"
            component={NextAdventure}
            roles={["Admin", "User"]}
          /> */}
          
          {/* 404 Catch-all route - must be last */}
          <Route path="*">
            <NotFound />
          </Route>
        </Switch>
      </Box>
    </Box>
  );
};

const App = () => {
  return (
    <HelmetProvider>
      <UserProvider>
        <GoogleAuthProvider>
          <OTPProvider>
            <ScrollProvider>
              <CurrencyProvider>
                <TranslationProvider>
                  <Router>
                    <ScrollToTop />
                    <AppContent />
                  </Router>
                </TranslationProvider>
              </CurrencyProvider>
            </ScrollProvider>
          </OTPProvider>
        </GoogleAuthProvider>
      </UserProvider>
    </HelmetProvider>
  );
};

export default App;
